import { useCallback } from 'react'

import {
  Channel,
  QueueStatsFilterRequest,
  RangeDirection,
  TimeScale,
  VolumeAggregationDimension,
  VolumeComparisonType,
} from 'modules/Monitoring/types'
import { UpdateStateHandler } from 'modules/Monitoring/url-state-storage/types'

import { AggregationDimensionValue } from '../../TicketsVolumeDetails'
import { TicketsVolumeDetailsControls } from '../TicketsVolumeDetailsContext'
import { CalculateRangeUpdatesHandler } from '../types'
import { checkFutureDateRangeSupported } from '../utils/checkFutureDateRangeSupported'
import { getBaseDimensionValue } from '../utils/getBaseDimensionValue'

import { useTimeRangeData } from './useTimeRangeData'

type VolumeHandlersParams = {
  state: TicketsVolumeDetailsControls
  updateState: UpdateStateHandler<TicketsVolumeDetailsControls>
  preparedFiltersForRequest: QueueStatsFilterRequest | undefined
  channel: Channel
  clearAdditionalFilters: VoidFunction
}

export const useTicketsVolumeHandlers = ({
  state,
  updateState,
  preparedFiltersForRequest,
  channel,
  clearAdditionalFilters,
}: VolumeHandlersParams) => {
  const { timeRangePages, currentTimeRangeIndex } = state

  const {
    currentTimeRange,
    calculateNextRange,
    calculateTimeRangeParams,
    hasNext: hasNextRange,
  } = useTimeRangeData({
    timeRangePages: timeRangePages || [],
    currentTimeRangeIndex: currentTimeRangeIndex || 0,
  })

  const handleCalculateRangeUpdates = useCallback<CalculateRangeUpdatesHandler>(
    params => {
      const supportsFuture = checkFutureDateRangeSupported({
        timeScale: params.timeScale,
        comparisonType: params.comparisonType,
      })

      return calculateTimeRangeParams({
        timeScale: params.timeScale,
        supportsFuture,
        forceSetFutureRange: params.forceSetFutureRange,
        timeframeFilter: preparedFiltersForRequest,
      })
    },
    [calculateTimeRangeParams, preparedFiltersForRequest],
  )

  const handleTimeScaleChange = useCallback(
    (newTimeScale: TimeScale) => {
      updateState(previousState => {
        let newComparisonType = previousState.comparisonType

        if (
          newTimeScale !== TimeScale.Hourly &&
          previousState.comparisonType === VolumeComparisonType.Forecasts
        ) {
          newComparisonType = VolumeComparisonType.LastWeek
        }

        const rangeUpdates = handleCalculateRangeUpdates({
          timeScale: newTimeScale,
          comparisonType: newComparisonType,
        })

        return {
          timeScale: newTimeScale,
          comparisonType: newComparisonType,
          ...rangeUpdates,
        }
      })
    },
    [updateState, handleCalculateRangeUpdates],
  )

  const handleComparisonTypeChange = useCallback(
    (newComparisonType: VolumeComparisonType) => {
      updateState(previousState => {
        let newTimeScale = previousState.timeScale
        let newAggregationDimension = previousState.aggregationDimension
        let newRange = {}
        let forceSetFutureRange = false

        if (newComparisonType === VolumeComparisonType.Forecasts) {
          newTimeScale = TimeScale.Hourly
          forceSetFutureRange = true
          newRange = handleCalculateRangeUpdates({
            timeScale: newTimeScale,
            comparisonType: newComparisonType,
            forceSetFutureRange,
          })
          clearAdditionalFilters()

          if (
            previousState.aggregationDimension.dimension !==
            VolumeAggregationDimension.Specialisation
          ) {
            newAggregationDimension = getBaseDimensionValue(channel)
          }
        } else if (previousState.comparisonType === VolumeComparisonType.Forecasts) {
          newRange = handleCalculateRangeUpdates({
            timeScale: newTimeScale,
            comparisonType: newComparisonType,
          })
        }

        return {
          comparisonType: newComparisonType,
          timeScale: newTimeScale,
          aggregationDimension: newAggregationDimension,
          ...newRange,
        }
      })
    },
    [channel, updateState, handleCalculateRangeUpdates, clearAdditionalFilters],
  )

  const handleAggregationDimensionChange = useCallback(
    (newAggregationDimension: AggregationDimensionValue) => {
      updateState(previousState => {
        let newComparisonType = previousState.comparisonType

        if (
          newAggregationDimension.dimension !==
            VolumeAggregationDimension.Specialisation &&
          previousState.comparisonType === VolumeComparisonType.Forecasts
        ) {
          newComparisonType = VolumeComparisonType.LastWeek
        }

        return {
          aggregationDimension: newAggregationDimension,
          comparisonType: newComparisonType,
        }
      })
    },
    [updateState],
  )

  const handleCurrentTimeRangeChange = useCallback(
    (direction: RangeDirection) => {
      updateState(previousState => {
        let newComparisonType = previousState.comparisonType

        const supportsFuture = checkFutureDateRangeSupported({
          timeScale: previousState.timeScale,
          comparisonType: previousState.comparisonType,
        })

        const nextRangeData = calculateNextRange({
          direction,
          timeScale: previousState.timeScale,
          comparisonType: previousState.comparisonType,
        })

        if (!nextRangeData) {
          return undefined
        }

        if (supportsFuture) {
          if (direction === RangeDirection.Next && nextRangeData.isLastPage) {
            newComparisonType = VolumeComparisonType.Forecasts
            clearAdditionalFilters()
          } else if (
            direction === RangeDirection.Previous &&
            previousState.comparisonType === VolumeComparisonType.Forecasts
          ) {
            newComparisonType = VolumeComparisonType.LastWeek
          }
        }

        return {
          ...nextRangeData,
          comparisonType: newComparisonType,
        }
      })
    },
    [calculateNextRange, updateState, clearAdditionalFilters],
  )

  return {
    currentTimeRange,
    hasNextRange,
    handleCalculateTimeRangeParams: calculateTimeRangeParams,
    handleCalculateRangeUpdates,
    handleTimeScaleChange,
    handleComparisonTypeChange,
    handleAggregationDimensionChange,
    handleCurrentTimeRangeChange,
  }
}
